import { cacheKeys } from "config";
import { isEqual } from "lodash";
import { surveysClient } from "modules/Surveys/client/surveysClient";
import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import { mainSurveyClient } from "../client/mainSurveyClient";
import { BlockValues, blockValuesToBlock, removeNonChangedValues } from "../helpers/blockForm";
import { SurveyBlocks } from "../surveyBlocks";

const UpdateTimeoutTime = 1000;

export const useBlockFormValues = (
  blockId?: string | number,
  parentSurveyId?: string | number,
  isMainSurvey?: boolean
) => {
  const [blockValues, setBlockValues] = useState<BlockValues>({});
  const [lastBlockValues, setLastBlockValues] = useState<BlockValues>({});
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<Record<string, Record<string, string[]>> | null>(null);

  const updateTimeout = useRef<NodeJS.Timeout | undefined>(undefined);
  const queryClient = useQueryClient();

  const updateBlockApi = useCallback(async (
      isWithoutTimeout: boolean = false,
      callback?: (blockValues: BlockValues, block?: SurveyBlocks.Block) => void
    ) => {
      if (!blockId || !parentSurveyId) {
        return;
      }

      clearTimeout(updateTimeout.current);
      setError(null);

      setIsSaving(false);
      if (!isEqual(blockValues, lastBlockValues)) {
        setIsSaving(true);
        const blockValuesApi = blockValuesToBlock(removeNonChangedValues(blockValues, lastBlockValues), !!isMainSurvey);

        const updateFunction = async () => {
          let updatedBlock: SurveyBlocks.Block | undefined;
          try {
            if (!isMainSurvey) {
              updatedBlock = await mainSurveyClient.updateSurveyBlock(parentSurveyId, blockId, blockValuesApi);
              queryClient.invalidateQueries(cacheKeys.mainSurvey.getSurveyBlock);
            } else {
              await surveysClient.updateSurvey(blockId, blockValues);
            }

            callback?.(blockValues, updatedBlock);
            setLastBlockValues(blockValues);
            setIsSaving(false);
            // TODO: add error handling
          } catch (error) {
            setIsSaving(false);
            // @ts-expect-error - error in catch block is unknown, but we expect it to be an AxiosError
            setError(error.response?.data);
            // @ts-expect-error - error in catch block is unknown, but we expect it to be an AxiosError
            console.error(error.response.data);
          }
        };

        if (isWithoutTimeout) {
          updateFunction();
        } else {
          updateTimeout.current = setTimeout(updateFunction, UpdateTimeoutTime);
        }
      }
    },
    [blockId, parentSurveyId, blockValues, lastBlockValues, isMainSurvey, queryClient]
  );

  useEffect(() => {
    setError(null)
  }, [blockId]);

  return {
    blockValues,
    setLastBlockValues,
    isSaving,
    setBlockValues,
    updateBlockApi,
    error,
  };
};

export type BlockFormValuesStore = ReturnType<typeof useBlockFormValues>;
